import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { useTranslation } from "react-i18next";
import { FaSearch } from "react-icons/fa";

/**
 * SysTable — drop-in replacement for GridTable.
 * Accepts the same `columns` and `rows` props as @nadavshaar/react-grid-table,
 * renders a PrimeReact DataTable with global search and pagination.
 *
 * Column shape: { id, label, field, width?, cellRenderer?, pinned? }
 */
const SysTable = ({ columns = [], rows = [], pageSizes = [10, 15, 20, 30] }) => {
  const { t, i18n } = useTranslation();
  const isRtl = ["ar", "ku"].includes(i18n.language?.split("-")[0]);
  const [search, setSearch] = useState("");

  const searchFields = columns.filter((c) => c.field).map((c) => c.field);

  const filteredRows =
    search.trim() === ""
      ? rows
      : rows.filter((row) =>
          searchFields.some((field) => {
            const val = row[field];
            return val != null && String(val).toLowerCase().includes(search.toLowerCase());
          })
        );

  const header = (
    <div className="sys-table-header">
      <span className="sys-table-search">
        <FaSearch className="sys-table-search-icon" />
        <InputText
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("table.search", "Search...")}
          className="sys-table-search-input"
        />
      </span>
    </div>
  );

  return (
    <DataTable
      value={filteredRows}
      paginator
      rows={pageSizes[0]}
      rowsPerPageOptions={pageSizes}
      header={header}
      emptyMessage={t("table.noRecords", "No records found")}
      stripedRows
      responsiveLayout="scroll"
      className="sys-datatable"
      dir={isRtl ? "rtl" : "ltr"}
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      currentPageReportTemplate={t(
        "table.pageReport",
        "Showing {first} to {last} of {totalRecords}"
      )}
      dataKey="ID"
      removableSort
    >
      {columns.map((col) =>
        col.cellRenderer ? (
          <Column
            key={col.id}
            header={col.label}
            body={(rowData) => col.cellRenderer({ data: rowData })}
            style={{ width: col.width, minWidth: col.width }}
            frozen={!!col.pinned}
            alignFrozen="right"
            exportable={false}
          />
        ) : (
          <Column
            key={col.id}
            field={col.field}
            header={col.label}
            sortable
            style={{ minWidth: col.width || "100px" }}
          />
        )
      )}
    </DataTable>
  );
};

export default SysTable;
